import InVivoError from '@/model/InVivoError';
import { ExceptionHandler } from '@/utils/ExceptionHandler';

export interface SseStreamCallbacks<T> {
  onEvent: (data: T) => void;
  onConnect?: () => void;
  onError?: () => void;
}

export interface SseConnection {
  close: () => void;
}

/**
 * Creates a typed EventSource connection to a messaging service SSE endpoint.
 * Handles URL construction, credential forwarding, JSON parsing, and error reporting.
 *
 * On any error the EventSource is closed immediately, delegating all reconnection
 * timing to the caller. This prevents the browser's built-in retry from racing
 * with hook-level backoff logic.
 */
const createSseConnection = <T>(path: string, callbacks: SseStreamCallbacks<T>): SseConnection => {
  const url = new URL(path, window.AppConfig.messagingApiUrl);
  const eventSource = new EventSource(url.href, { withCredentials: true });

  eventSource.onopen = () => {
    callbacks.onConnect?.();
  };

  eventSource.onmessage = (messageEvent: MessageEvent) => {
    try {
      const parsed = JSON.parse(messageEvent.data) as T;
      callbacks.onEvent(parsed);
    } catch (error) {
      ExceptionHandler.captureException(
        new InVivoError('Failed to parse SSE event', {
          cause: error instanceof Error ? error : new Error(String(error)),
          slug: 'sse-parse',
          level: 'warning',
          context: { path },
        })
      );
    }
  };

  eventSource.onerror = () => {
    eventSource.close();
    callbacks.onError?.();
  };

  return {
    close: () => {
      eventSource.close();
    },
  };
};

export const MessagingService = { createSseConnection };
